import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";

import api, { API_BASE, formatApiErrorDetail, getAuthToken } from "@/lib/api";
import { ModuleTitleTRAINING } from "@/components/training/ModuleTitleTRAINING";

const STATUS_LABEL = {
  not_started: "Not started",
  in_progress: "In progress",
  completed: "Completed",
};
const STATUS_PILL = {
  not_started: "bg-sage-300/40 text-forest-800 border-sage-300",
  in_progress: "bg-terracotta-500/15 text-terracotta-600 border-terracotta-500/30",
  completed: "bg-forest-600 text-cream-100 border-forest-800",
};

export default function TrainingCompliance() {
  const [trainings, setTrainings] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({
    training_id: "",
    campus_id: "",
    department: "",
    user_type: "all",
  });
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/training/trainings")
      .then((r) => setTrainings(r.data.filter((t) => !t.archived)))
      .catch(() => setTrainings([]));
    api.get("/campuses").then((r) => setCampuses(r.data)).catch(() => {});
    api.get("/users/directory")
      .then((r) => {
        const set = new Set();
        (r.data || []).forEach((u) => u.department && set.add(u.department));
        setDepartments(Array.from(set));
      })
      .catch(() => {});
  }, []);

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/training/reports/compliance", { params });
      setReport(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
     
  }, [filters]);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const url = `${API_BASE}/training/reports/compliance.csv${params.toString() ? `?${params}` : ""}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dl;
      a.download = `mypatria-training-compliance-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
      toast.success("CSV downloaded.");
    } catch (err) {
      toast.error(err.message || "Download failed");
    }
  };

  const rate = report?.completion_rate_pct ?? 0;

  return (
    <div className="space-y-6" data-testid="training-compliance-page">
      <div>
        <ModuleTitleTRAINING />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Compliance Dashboard</h1>
        <p className="mt-2 text-charcoal-soft max-w-2xl">
          Real-time completion snapshot across every training. Export CSV for record retention.
        </p>
      </div>

      <section className="patria-card p-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <select
          className="patria-input text-sm"
          value={filters.training_id}
          onChange={(e) => setFilters({ ...filters, training_id: e.target.value })}
          data-testid="training-filter-training"
        >
          <option value="">All Trainings</option>
          {trainings.map((t) => (
            <option key={t.id} value={t.id}>{t.title}</option>
          ))}
        </select>
        <select
          className="patria-input text-sm"
          value={filters.campus_id}
          onChange={(e) => setFilters({ ...filters, campus_id: e.target.value })}
          data-testid="training-filter-campus"
        >
          <option value="">All Campuses</option>
          {campuses.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select
          className="patria-input text-sm"
          value={filters.department}
          onChange={(e) => setFilters({ ...filters, department: e.target.value })}
          data-testid="training-filter-department"
        >
          <option value="">All Departments</option>
          {departments.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <select
          className="patria-input text-sm"
          value={filters.user_type}
          onChange={(e) => setFilters({ ...filters, user_type: e.target.value })}
          data-testid="training-filter-user-type"
        >
          <option value="all">Staff + Volunteers</option>
          <option value="staff">Staff Only</option>
          <option value="volunteers">Volunteers Only</option>
        </select>
      </section>

      <section className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        {[
          { label: "Assigned", value: report?.total ?? 0 },
          { label: "Completed", value: report?.completed ?? 0 },
          { label: "In progress", value: report?.in_progress ?? 0 },
          { label: "Not started", value: report?.not_started ?? 0 },
          { label: "Overdue", value: report?.overdue ?? 0, warn: (report?.overdue ?? 0) > 0 },
        ].map((s) => (
          <div
            key={s.label}
            className={`patria-card p-4 ${s.warn ? "border-l-4 border-terracotta-500" : ""}`}
            data-testid={`training-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <p className="text-xs uppercase tracking-widest text-charcoal-muted">{s.label}</p>
            <p className="font-serif text-forest-900 text-3xl mt-1">{s.value}</p>
          </div>
        ))}
      </section>

      <section className="patria-card p-5">
        <p className="text-xs uppercase tracking-widest text-charcoal-muted">
          Overall completion rate
        </p>
        <div className="mt-3 h-4 rounded-full bg-sage-300/50 overflow-hidden">
          <div
            className="h-full bg-forest-600"
            style={{ width: `${rate}%` }}
            data-testid="training-completion-bar"
          />
        </div>
        <p className="mt-2 text-sm text-charcoal-soft">
          <b className="text-forest-900">{rate}%</b> of assignments are complete.
        </p>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="patria-card p-5">
          <h3 className="font-serif text-forest-900 text-lg">By Training</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {(report?.by_training || []).map((b) => (
              <li key={b.training_id} className="flex items-center justify-between">
                <span className="text-charcoal">{b.title}</span>
                <span className="text-forest-900 font-medium">{b.completed}/{b.assigned}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="patria-card p-5">
          <h3 className="font-serif text-forest-900 text-lg">By Campus</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {(report?.by_campus || []).map((b) => (
              <li key={b.campus} className="flex items-center justify-between">
                <span className="text-charcoal">{b.campus}</span>
                <span className="text-forest-900 font-medium">{b.completed}/{b.assigned}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="patria-card p-5">
          <h3 className="font-serif text-forest-900 text-lg">By Department</h3>
          <ul className="mt-3 space-y-2 text-sm">
            {(report?.by_department || []).map((b) => (
              <li key={b.department} className="flex items-center justify-between">
                <span className="text-charcoal">{b.department}</span>
                <span className="text-forest-900 font-medium">{b.completed}/{b.assigned}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="flex flex-col items-end gap-1">
        <button
          onClick={downloadCsv}
          disabled={busy}
          className="patria-btn-primary"
          data-testid="training-csv-export"
        >
          <Download className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
          Export CSV
        </button>
        <p className="text-xs text-charcoal-muted italic max-w-md text-right">
          Records retained at least 2 years per California harassment-prevention
          training recordkeeping requirements.
        </p>
      </section>

      <section className="patria-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-forest-600 text-cream-100">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Teammate</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-left px-4 py-3 font-medium">Training</th>
              <th className="text-left px-4 py-3 font-medium">Required</th>
              <th className="text-left px-4 py-3 font-medium">Status</th>
              <th className="text-left px-4 py-3 font-medium">Due</th>
              <th className="text-left px-4 py-3 font-medium">Completed</th>
              <th className="text-left px-4 py-3 font-medium">Best Quiz</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-sage-300/60">
            {(report?.assignments || []).slice(0, 200).map((a) => (
              <tr key={a.id} data-testid={`training-comp-row-${a.id}`}>
                <td className="px-4 py-3">
                  <p className="text-forest-900 font-medium">{a.user_name}</p>
                  <p className="text-xs text-charcoal-muted">{a.department}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={a.user_type === "volunteer" ? "patria-chip-terra" : "patria-chip-green"}>
                    {a.user_type === "volunteer" ? "Volunteer" : "Staff"}
                  </span>
                </td>
                <td className="px-4 py-3">{a.training_title}</td>
                <td className="px-4 py-3">{a.required ? "Yes" : "No"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${STATUS_PILL[a.status]}`}>
                    {STATUS_LABEL[a.status]}
                  </span>
                </td>
                <td className={`px-4 py-3 ${a.overdue ? "text-terracotta-600 font-medium" : "text-charcoal-soft"}`}>
                  {a.due_date ? (a.due_date || "").split("T")[0] : "—"}
                  {a.overdue && " • OVERDUE"}
                </td>
                <td className="px-4 py-3 text-charcoal-soft">
                  {a.completed_at ? (a.completed_at || "").split("T")[0] : "—"}
                </td>
                <td className="px-4 py-3 text-charcoal-soft">
                  {a.best_score !== null && a.best_score !== undefined ? `${a.best_score}%` : "—"}
                </td>
              </tr>
            ))}
            {(!report || report.assignments.length === 0) && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-sm text-charcoal-muted italic">
                  No assignments match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
